import { useState, useEffect, useRef } from "react";
import classNames from "classnames";
import { Link, useLocation } from "react-router-dom";
import { RampUnit, TagBasedAdUnits, TaglessAdUnits, NamedAdUnits } from "@outplayed/ramp";
import { AppRoutes } from "@ugg/shared/routes/app-routes";

const isProduction = process.env.NODE_ENV === "production";
const devMode = true && !isProduction;

const PLUS_UTM = "utm_source=uggbuilds";
const PLUS_EXTERNAL_URL = `https://u.gg/plus?${PLUS_UTM}`;

type RampInContentContainerType = {
  className?: string;
  id?: string;
  unitId?: string;
  type?: TagBasedAdUnits | TaglessAdUnits | NamedAdUnits;
  header?: string;
  showAdLabel?: boolean;
  showPlusLink?: boolean;
  slotClassName?: string;
  inNewWindow?: boolean;
  ignoreSkinAd?: boolean;
};

export const RampInContentContainer = ({
  className,
  id,
  unitId,
  type,
  header,
  showAdLabel,
  showPlusLink,
  slotClassName,
  inNewWindow,
  ignoreSkinAd,
}: RampInContentContainerType) => {
  const hasChrome = !!showAdLabel || !!showPlusLink;

  return (
    <div
      className={classNames(
        "flex flex-col rounded-[3px]",
        // Chrome variant: the inset lives on the header row only so the ad slot sits flush to the
        // wrapper edges; overflow-hidden keeps the flush ad within the rounded corners.
        hasChrome ? "bg-purple-200 overflow-hidden" : "bg-[#191937] flex-column gap-y-[6px]",
        {
          "px-[18px] pb-[18px] pt-[12px]": !!header && !hasChrome,
          "p-[18px]": !header && !hasChrome,
        },
        className,
      )}
    >
      {hasChrome && (
        <div className="flex items-center justify-between px-[12px] py-[6px] text-[10px] leading-none bg-purple-400">
          {showAdLabel && <span className="text-lavender-50">Advertisement</span>}
          {showPlusLink && (
            <Link
              to={`${AppRoutes.PLUS}?${PLUS_UTM}`}
              onClick={
                inNewWindow
                  ? (e) => {
                      e.preventDefault();
                      window.open(PLUS_EXTERNAL_URL);
                    }
                  : undefined
              }
              className="ml-auto text-accent-yellow-400"
            >
              Go Ad-Free
            </Link>
          )}
        </div>
      )}
      {header && !hasChrome && <div className="text-white text-[14px] h-[18px]">{header}</div>}
      <RampInContent id={id} unitId={unitId} type={type} slotClassName={slotClassName} ignoreSkinAd={ignoreSkinAd} />
    </div>
  );
};

function useSkinAdDetection() {
  const polling = useRef<NodeJS.Timer>();
  const [skinAdActive, setSkinAdActive] = useState(false);

  useEffect(() => {
    polling.current = setInterval(() => {
      const skinDiv = document.getElementById("siteSkinContainer");
      setSkinAdActive(!!skinDiv);
    }, 1000);

    return () => {
      polling.current && clearInterval(polling.current);
    };
  }, []);

  return skinAdActive;
}

interface RampInContentProps {
  className?: string;
  id?: string;
  unitId?: string;
  type?: TagBasedAdUnits | TaglessAdUnits | NamedAdUnits;
  slotClassName?: string;
  ignoreSkinAd?: boolean;
}

const RampInContent = ({
  id = "ugg-in-content",
  unitId = "pw-in-content",
  type = TaglessAdUnits.InContent,
  slotClassName = "h-[300px] w-full",
  ignoreSkinAd,
}: RampInContentProps) => {
  const location = useLocation();
  const skinAdActive = useSkinAdDetection();

  return (
    <div id={id} className={classNames("flex flex-col items-center w-full")}>
      {(ignoreSkinAd || !skinAdActive) && (
        <RampUnit
          id={unitId}
          type={type}
          className={classNames(slotClassName, {
            "bg-red-500 bg-opacity-50": devMode,
          })}
          pathname={location.pathname}
          search={location.search}
        />
      )}
    </div>
  );
};
